import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { CheckCircle, Loader, AlertCircle } from 'lucide-react';

const PaymentSuccessPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [subscriptionStatus, setSubscriptionStatus] = useState('loading');
    const [subscriptionType, setSubscriptionType] = useState(null);

    useEffect(() => {
        const checkSubscription = async () => {
            if (!currentUser) {
                navigate('/login');
                return;
            }

            try {
                // Așteaptă câteva secunde pentru ca webhook-ul să proceseze
                await new Promise(resolve => setTimeout(resolve, 3000));

                // Verifică subscription în userResponses
                const userResponsesRef = doc(db, 'userResponses', currentUser.uid);
                const userResponsesSnap = await getDoc(userResponsesRef);

                if (userResponsesSnap.exists()) {
                    const data = userResponsesSnap.data();
                    const subType = data.subscription;

                    if (subType && subType !== 'none') {
                        setSubscriptionType(subType);
                        setSubscriptionStatus('success');
                    } else {
                        // Încearcă din nou după 5 secunde (webhook-ul poate fi lent)
                        setTimeout(async () => {
                            const retrySnap = await getDoc(userResponsesRef);
                            if (retrySnap.exists()) {
                                const retryData = retrySnap.data();
                                const retrySubType = retryData.subscription;

                                if (retrySubType && retrySubType !== 'none') {
                                    setSubscriptionType(retrySubType);
                                    setSubscriptionStatus('success');
                                } else {
                                    setSubscriptionStatus('pending');
                                }
                            } else {
                                setSubscriptionStatus('pending');
                            }
                        }, 5000);
                    }
                } else {
                    setSubscriptionStatus('pending');
                }
            } catch (error) {
                console.error('Eroare la verificarea abonamentului:', error);
                setSubscriptionStatus('error');
            }
        };

        checkSubscription();
    }, [currentUser, navigate]);

    const getSubscriptionName = (type) => {
        switch (type) {
            case 'standard':
                return 'Standard';
            case 'premium':
                return 'Premium';
            default:
                return '';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-green-50 to-slate-50 flex items-center justify-center px-6 py-12">
            <div className="max-w-2xl w-full">
                {/* Loading State */}
                {subscriptionStatus === 'loading' && (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <div className="flex justify-center mb-6">
                            <Loader className="w-16 h-16 text-blue-600 animate-spin" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-4">
                            Se procesează plata...
                        </h1>
                        <p className="text-lg text-slate-600">
                            Verificăm status-ul abonamentului tău. Vă rugăm așteptați.
                        </p>
                    </div>
                )}

                {/* Success State */}
                {subscriptionStatus === 'success' && (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <div className="flex justify-center mb-6">
                            <CheckCircle className="w-20 h-20 text-green-600" />
                        </div>
                        <h1 className="text-4xl font-bold text-slate-900 mb-4">
                            Plata a fost finalizată cu succes! 🎉
                        </h1>
                        <p className="text-xl text-slate-600 mb-8">
                            Abonamentul tău <span className="font-bold text-green-600">{getSubscriptionName(subscriptionType)}</span> este acum activ.
                        </p>

                        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
                            <h2 className="font-semibold text-slate-900 mb-3">Ce urmează?</h2>
                            <ul className="text-left text-slate-700 space-y-2">
                                <li className="flex items-start">
                                    <span className="text-green-600 mr-2">✓</span>
                                    <span>Ai acces complet la toate testele din planul {getSubscriptionName(subscriptionType)}</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-600 mr-2">✓</span>
                                    <span>Rezultatele tale vor fi salvate automat în cloud</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-600 mr-2">✓</span>
                                    <span>Vei primi analize personalizate după completarea testelor</span>
                                </li>
                            </ul>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => navigate('/teste')}
                                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-8 rounded-lg transition text-lg"
                            >
                                Începe testele
                            </button>
                            <button
                                onClick={() => navigate('/')}
                                className="bg-slate-200 hover:bg-slate-300 text-slate-900 font-semibold py-4 px-8 rounded-lg transition text-lg"
                            >
                                Înapoi acasă
                            </button>
                        </div>

                        <p className="text-sm text-slate-500 mt-8">
                            Vei primi un email de confirmare de la Stripe în câteva minute.
                        </p>
                    </div>
                )}

                {/* Pending State - când webhook-ul nu a procesat încă */}
                {subscriptionStatus === 'pending' && (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <div className="flex justify-center mb-6">
                            <AlertCircle className="w-16 h-16 text-yellow-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-4">
                            Plata este în curs de procesare
                        </h1>
                        <p className="text-lg text-slate-600 mb-6">
                            Plata ta a fost acceptată, dar abonamentul este încă în curs de activare. Acest proces poate dura până la 5 minute.
                        </p>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
                            <p className="text-slate-700">
                                <strong>Ce să faci acum:</strong>
                            </p>
                            <ul className="text-left text-slate-700 space-y-2 mt-3">
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>Poți închide această pagină - abonamentul va fi activat automat</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>Verifică email-ul pentru confirmarea de la Stripe</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>Dacă problema persistă mai mult de 10 minute, contactează suportul</span>
                                </li>
                            </ul>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-lg transition"
                            >
                                Reîncarcă pagina
                            </button>
                            <button
                                onClick={() => navigate('/')}
                                className="bg-slate-200 hover:bg-slate-300 text-slate-900 font-semibold py-3 px-6 rounded-lg transition"
                            >
                                Înapoi acasă
                            </button>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {subscriptionStatus === 'error' && (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <div className="flex justify-center mb-6">
                            <AlertCircle className="w-16 h-16 text-red-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-4">
                            A apărut o problemă
                        </h1>
                        <p className="text-lg text-slate-600 mb-6">
                            Nu am putut verifica status-ul abonamentului tău. Plata ta ar putea fi în curs de procesare.
                        </p>

                        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
                            <p className="text-slate-700 mb-3">
                                <strong>Ce să faci:</strong>
                            </p>
                            <ul className="text-left text-slate-700 space-y-2">
                                <li>1. Verifică email-ul pentru confirmarea de la Stripe</li>
                                <li>2. Așteaptă 5-10 minute și reîncarcă pagina</li>
                                <li>3. Dacă problema persistă, contactează suportul</li>
                            </ul>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition"
                            >
                                Încearcă din nou
                            </button>
                            <button
                                onClick={() => navigate('/pricing')}
                                className="bg-slate-200 hover:bg-slate-300 text-slate-900 font-semibold py-3 px-6 rounded-lg transition"
                            >
                                Înapoi la Pricing
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccessPage;